'use client'
import React, { useState, useRef, useEffect } from 'react'

type Items = any[]
type ShowMoreActionProps = { count: number }

export interface ShowMoreProps {
  items: Items
  loadItemsCount: number
  defaultVisibleItems: number
  renderItem?: (item: any) => React.ReactNode
  renderAction?: (props: {
    more: (props?: Partial<ShowMoreActionProps>) => void
    reset: () => void
    isMore: boolean
    isReset: boolean
    noMore: boolean
  }) => React.ReactNode
}

export default function Component({
  items: data,
  loadItemsCount,
  defaultVisibleItems,
  renderItem,
  renderAction,
}: ShowMoreProps) {
  const [items, setItems] = useState<Items>([])
  const [visibleItems, setVisibleItems] = useState(defaultVisibleItems || 3)
  const containerRef = useRef(null)

  const more = (props?: Partial<ShowMoreActionProps>) =>
    setVisibleItems((prevVisibleItems) => prevVisibleItems + (props?.count || loadItemsCount))

  const reset = () => setVisibleItems(defaultVisibleItems || 3)

  useEffect(() => {
    if (data) setItems(data)
  }, [data])

  useEffect(() => {
    if (defaultVisibleItems) setVisibleItems(defaultVisibleItems)
  }, [defaultVisibleItems])

  useEffect(() => {
    if (containerRef.current)
      (containerRef.current as any)?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
  }, [visibleItems])

  const isMore = visibleItems < items.length
  const isReset = !isMore && defaultVisibleItems < items.length
  const noMore = defaultVisibleItems >= items.length

  return (
    <div ref={containerRef}>
      <div className="flex flex-wrap gap-x-1 gap-y-2">
        {items.slice(0, visibleItems).map((item, index) => (
          <div key={index} className="w-full animate-fadeInUp">
            {renderItem && renderItem(item)}
          </div>
        ))}
      </div>
      {renderAction && renderAction({ reset, more, isMore, isReset, noMore })}
    </div>
  )
}
